import { useEffect, useState } from "react";
import { api } from "../lib/api.ts";
import { useBrands } from "../lib/brand.tsx";
import { Icon } from "../lib/icons.tsx";
import { ADS_PRESETS, type AdsDiagnosisOutput, type MetricRead } from "@shared/ads.ts";

const METRIC_FIELDS: { key: string; label: string; unit?: string }[] = [
  { key: "objective", label: "วัตถุประสงค์" },
  { key: "timeRange", label: "ช่วงเวลา", unit: "" },
  { key: "dailyBudget", label: "งบ/วัน", unit: "฿" },
  { key: "spend", label: "ใช้จ่ายรวม", unit: "฿" },
  { key: "impression", label: "Impression" },
  { key: "reach", label: "Reach" },
  { key: "click", label: "คลิก" },
  { key: "ctr", label: "CTR", unit: "%" },
  { key: "cpc", label: "CPC", unit: "฿" },
  { key: "cpm", label: "CPM", unit: "฿" },
  { key: "frequency", label: "Frequency" },
  { key: "lead", label: "Lead" },
  { key: "message", label: "ทักแชต" },
  { key: "cpl", label: "CPL", unit: "฿" },
  { key: "sale", label: "ยอดขาย (จำนวน)" },
  { key: "revenue", label: "รายได้", unit: "฿" },
  { key: "roas", label: "ROAS" },
];
const TEXT_METRICS = new Set(["objective", "timeRange"]);
const CONTEXT_FIELDS = [
  { key: "creative", label: "Creative (อธิบายสั้นๆ)" },
  { key: "offer", label: "Offer" },
  { key: "audience", label: "Audience/Interest ที่ยิง" },
  { key: "landingPage", label: "หน้า Landing/รับ Lead" },
];
const TONE: Record<MetricRead["tone"], string> = { ok: "var(--green-600)", warn: "var(--amber-600)", bad: "var(--red-600)", neutral: "var(--gray-500)" };
const SEV: Record<string, string> = { high: "mh-pill--problem", medium: "mh-pill--watch", low: "mh-pill--neutral" };
const CONF: Record<string, string> = { high: "mh-conf--high", medium: "mh-conf--medium", low: "mh-conf--low" };

export default function AdsDoctor() {
  const { activeBrand } = useBrands();
  const [preset, setPreset] = useState("auto");
  const [metrics, setMetrics] = useState<Record<string, string>>({});
  const [context, setContext] = useState<Record<string, string>>({});
  const [out, setOut] = useState<AdsDiagnosisOutput | null>(null);
  const [isMock, setIsMock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [savedLearn, setSavedLearn] = useState(false);

  useEffect(() => { loadHistory(); }, [activeBrand?.id]);
  async function loadHistory() {
    if (!activeBrand) return setHistory([]);
    const r = await api.get<{ diagnoses: any[] }>(`/ads/diagnoses?brandId=${activeBrand.id}`);
    setHistory(r.diagnoses);
  }

  const setM = (k: string, v: string) => setMetrics((p) => ({ ...p, [k]: v }));
  const setC = (k: string, v: string) => setContext((p) => ({ ...p, [k]: v }));

  async function diagnose() {
    if (!activeBrand) { setErr("เลือกแบรนด์ก่อน"); return; }
    setErr(null); setLoading(true); setOut(null); setSavedLearn(false);
    try {
      const num: Record<string, any> = {};
      for (const [k, v] of Object.entries(metrics)) {
        if (v === "") continue;
        num[k] = TEXT_METRICS.has(k) ? v : Number(v);
      }
      const r = await api.post<any>("/ads/diagnose", { brandId: activeBrand.id, preset, metrics: num, context });
      if (r.error === "ai_unparsable") { setErr("AI ตอบไม่เป็นรูปแบบ ลองใหม่"); return; }
      setOut(r.output); setIsMock(r.isMock); loadHistory();
    } catch (e: any) { setErr("ผิดพลาด: " + (e?.message || "")); }
    finally { setLoading(false); }
  }

  async function openDiag(id: string) {
    setLoading(true);
    try { const r = await api.get<{ diagnosis: any }>(`/ads/diagnoses/${id}`); setOut(r.diagnosis.data); setIsMock(false); }
    finally { setLoading(false); }
  }

  async function saveLearning() {
    if (!activeBrand || !out) return;
    await api.post("/learnings", { brandId: activeBrand.id, type: "ads", title: `Ads Doctor: ${out.mainDiagnosis.slice(0, 60)}`, worked: out.expectedSignal, sourceModule: "ads-doctor", confidence: out.confidence });
    setSavedLearn(true);
  }

  return (
    <div className="page">
      <div className="page__head mh-spread">
        <div>
          <div className="page__title">Ads Doctor <span className="mh-muted mh-eng" style={{ fontSize: 15 }}>หมอตรวจแอด</span></div>
          <div className="page__subtitle">{activeBrand ? <>บริบท: <b>{activeBrand.name}</b> — วินิจฉัยตามลำดับ ไม่ฟันธงจากตัวเลขเดียว</> : "เลือกแบรนด์ก่อน"}</div>
        </div>
        <Icon name="Stethoscope" size={22} />
      </div>

      <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
        <div className="form-grid" style={{ marginBottom: 4 }}>
          <div className="mh-field" style={{ margin: 0 }}>
            <label className="mh-label">อาการที่กังวล (Preset)</label>
            <select className="mh-select" value={preset} onChange={(e) => setPreset(e.target.value)}>
              {ADS_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
        </div>

        <div className="mh-label" style={{ marginTop: 14, marginBottom: 8 }}>เมทริกซ์ (กรอกเท่าที่มี)</div>
        <div className="form-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))" }}>
          {METRIC_FIELDS.map((f) => (
            <div className="mh-field" style={{ margin: 0 }} key={f.key}>
              <label className="mh-label" style={{ fontSize: 12.5 }}>{f.label} {f.unit && <span className="mh-muted">{f.unit}</span>}</label>
              <input className="mh-input" type={TEXT_METRICS.has(f.key) ? "text" : "number"} value={metrics[f.key] ?? ""} onChange={(e) => setM(f.key, e.target.value)} />
            </div>
          ))}
          <div className="mh-field" style={{ margin: 0 }}>
            <label className="mh-label" style={{ fontSize: 12.5 }}>คุณภาพ Lead</label>
            <select className="mh-select" value={metrics.leadQuality ?? ""} onChange={(e) => setM("leadQuality", e.target.value)}>
              <option value="">—</option><option value="ดี">ดี</option><option value="กลาง">กลาง</option><option value="แย่">แย่</option>
            </select>
          </div>
        </div>

        <div className="mh-label" style={{ marginTop: 14, marginBottom: 8 }}>บริบทเพิ่มเติม (ช่วยให้แม่นขึ้น)</div>
        <div className="form-grid">
          {CONTEXT_FIELDS.map((f) => (
            <div className="mh-field" style={{ margin: 0 }} key={f.key}>
              <label className="mh-label" style={{ fontSize: 12.5 }}>{f.label}</label>
              <input className="mh-input" value={context[f.key] ?? ""} onChange={(e) => setC(f.key, e.target.value)} />
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button className="mh-btn mh-btn--primary mh-btn--lg" onClick={diagnose} disabled={loading}>
            {loading ? <span className="spinner" /> : <><Icon name="Stethoscope" size={18} /> วินิจฉัย</>}
          </button>
          {history.length > 0 && (
            <select className="mh-select" style={{ width: "auto" }} defaultValue="" onChange={(e) => e.target.value && openDiag(e.target.value)}>
              <option value="">📁 ผลวินิจฉัยเดิม ({history.length})</option>
              {history.map((h) => <option key={h.id} value={h.id}>{h.mainDiagnosis?.slice(0, 40)}</option>)}
            </select>
          )}
          {err && <span className="mh-pill mh-pill--problem">{err}</span>}
        </div>
      </div>

      {loading && <div className="empty"><span className="spinner spinner--dark" /><p className="mh-sec">กำลังวินิจฉัย...</p></div>}

      {out && (
        <>
          {isMock && <div className="mock-banner"><Icon name="Wrench" size={18} /><div>วินิจฉัยด้วย <b>ตรรกะ Mock</b> — ใส่ Claude API Key เพื่อการวิเคราะห์เชิงลึกจริง</div></div>}

          <div className={"mh-card card-pad"} style={{ marginBottom: 14, borderLeft: `4px solid ${out.dataSufficiency.enough ? "var(--green-600)" : "var(--amber-600)"}` }}>
            <b>{out.dataSufficiency.enough ? "✅ ข้อมูลพอวินิจฉัย" : "⚠️ ข้อมูลยังน้อย"}</b> — <span className="mh-sec">{out.dataSufficiency.note}</span>
          </div>

          <div className="mh-card card-pad" style={{ marginBottom: 14 }}>
            <div className="mh-spread" style={{ alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <div className="mh-muted" style={{ fontSize: 12.5 }}>การวินิจฉัยหลัก (Main Diagnosis)</div>
                <div style={{ fontWeight: 700, fontSize: 18, marginTop: 4 }}>{out.mainDiagnosis}</div>
                {out.secondaryDiagnosis && <div className="mh-sec" style={{ marginTop: 8 }}>รอง: {out.secondaryDiagnosis}</div>}
              </div>
              <div style={{ display: "flex", gap: 6, flex: "none" }}>
                <span className={"mh-pill " + SEV[out.severity]}>รุนแรง: {out.severity}</span>
                <span className={"mh-conf " + CONF[out.confidence]}>{out.confidence}</span>
              </div>
            </div>
            <button className="mh-btn mh-btn--subtle mh-btn--sm" style={{ marginTop: 14 }} onClick={saveLearning} disabled={savedLearn}>
              {savedLearn ? "บันทึกแล้ว ✓" : <><Icon name="Save" size={15} /> Save as Learning</>}
            </button>
          </div>

          {out.metricReview?.length > 0 && (
            <div className="mh-card card-pad" style={{ marginBottom: 14 }}>
              <h3 style={{ fontSize: 14, marginBottom: 12 }}>อ่านความสัมพันธ์ของเมทริกซ์</h3>
              <div style={{ display: "grid", gap: 8 }}>
                {out.metricReview.map((mr, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, alignItems: "baseline", borderBottom: "1px solid var(--border)", paddingBottom: 8 }}>
                    <b style={{ minWidth: 90, color: TONE[mr.tone] }}>{mr.metric}</b>
                    <span style={{ minWidth: 60, fontWeight: 600 }}>{mr.value}</span>
                    <span className="mh-sec" style={{ fontSize: 13.5 }}>{mr.read}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-grid" style={{ alignItems: "start" }}>
            <Panel title="หลักฐาน (Evidence)" icon="Radar">
              <ul style={{ margin: 0, paddingLeft: 18 }}>{out.evidence.map((e, i) => <li key={i} style={{ marginBottom: 4 }}>{e}</li>)}</ul>
            </Panel>
            <Panel title="สิ่งที่ยังไม่ควรเปลี่ยน" icon="Scale" tone="warn">
              {out.doNotChangeYet}
            </Panel>
          </div>

          <div className="mh-card card-pad" style={{ margin: "14px 0" }}>
            <h3 style={{ fontSize: 14, marginBottom: 12 }}>คำแนะนำ (เรียงตามความสำคัญ)</h3>
            <div style={{ display: "grid", gap: 10 }}>
              {out.recommendedAction.map((a, i) => (
                <div key={i} style={{ display: "flex", gap: 12 }}>
                  <div className="qa__icon" style={{ width: 28, height: 28, flex: "none", fontWeight: 700 }}>{i + 1}</div>
                  <div><div style={{ fontWeight: 600 }}>{a.action}</div><div className="mh-sec" style={{ fontSize: 13.5 }}>เพราะ: {a.why}</div></div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-grid">
            <Panel title="แผนทดสอบ (Test Plan)" icon="Radar">{out.testPlan}</Panel>
            <Panel title="สัญญาณที่คาดหวัง" icon="TrendingUp">{out.expectedSignal}</Panel>
            <Panel title="เมื่อไรควรรีวิว" icon="Scale">{out.whenToReview}</Panel>
            <Panel title="คำอธิบายทางเลือก" icon="Sparkles">{out.alternativeExplanation}</Panel>
          </div>
        </>
      )}
    </div>
  );
}

function Panel({ title, icon, children, tone }: { title: string; icon: string; children: React.ReactNode; tone?: "warn" }) {
  return (
    <div className="mh-card card-pad" style={tone === "warn" ? { background: "var(--amber-050)", borderColor: "#f2dca0" } : undefined}>
      <div className="mh-row" style={{ gap: 8, marginBottom: 8, fontWeight: 700, fontSize: 14 }}><Icon name={icon} size={16} /> {title}</div>
      <div style={{ fontSize: 14, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}
